<script setup lang="ts">
import { ref, watch } from 'vue';
import Button from 'primevue/button';
import Tag from 'primevue/tag';
import { Stack, Typography } from '@apparatix/library/core';
import { ContactCard } from '@apparatix/leads/ContactCard';
import ContactAutoComplete from '@apparatix/leads/ContactAutoComplete.vue';
import type { MergeCandidate } from '@apparatix/types/Contacts';
import type { PendingContact } from '@apparatix/schemas/Contact';
import { WizardNewContact } from '@apparatix/schemas/CreateLeadWizard';

type Props = {
  candidates: MergeCandidate[];
  isLoading: boolean;
  sourceContactName?: string;
}

const props = defineProps<Props>();

const emit = defineEmits<{
  select: [contactId: number];
}>();

const searchedContact = ref<PendingContact | WizardNewContact>();

watch(searchedContact, contact => {
  const contactId = contact && typeof contact === 'object' && 'id' in contact ? Number(contact.id) : undefined;
  if (contactId) {
    emit('select', contactId);
  }
});

const matchSeverity = (match: string) => {
  switch (match) {
    case 'Name':
      return 'primary';
    case 'Email':
      return 'success';
    case 'Phone':
      return 'info';
    case 'Address':
      return 'warn';
    case 'Same Account':
      return 'contrast';
    default:
      return 'secondary';
  }
};
</script>
<template>
  <Stack :gap="5">
    <Stack :gap="2">
      <Typography variant="section-heading">
        Likely duplicates:
      </Typography>
      <Typography variant="small-subtitle" muted>
        These contacts share a name, email, phone number, or address with {{ props.sourceContactName || 'this contact' }}.
        The contact you select will be combined into {{ props.sourceContactName || 'this contact' }} and removed.
      </Typography>
    </Stack>
    <div v-if="props.isLoading" class="flex items-center gap-2">
      <i class="pi pi-spin pi-spinner" />
      <Typography variant="body">
        Looking for duplicates&hellip;
      </Typography>
    </div>
    <Typography v-else-if="!props.candidates.length" variant="body" muted>
      No likely duplicates were found. You can search for any other contact below.
    </Typography>
    <div v-else class="flex flex-col p-3 rounded-xl bg-[#F1F5F9]">
      <Stack :gap="4">
        <ContactCard
          v-for="candidate in props.candidates"
          :id="candidate.id"
          :key="candidate.id"
          :name="candidate.fullName"
          :company="candidate.companyName"
          :owner="candidate.ownerName"
        >
          <Stack :gap="2">
            <div class="flex flex-wrap items-center gap-2">
              <Typography variant="form-label">
                Matches on:
              </Typography>
              <Tag
                v-for="match in candidate.matchedOn"
                :key="match"
                :value="match"
                :severity="matchSeverity(match)"
              />
            </div>
            <Typography v-if="candidate.emails" variant="small-subtitle" muted>
              {{ candidate.emails }}
            </Typography>
            <Typography v-if="candidate.phoneNumbers" variant="small-subtitle" muted>
              {{ candidate.phoneNumbers }}
            </Typography>
            <Typography v-if="candidate.fullAddress" variant="small-subtitle" muted>
              {{ candidate.fullAddress }}
            </Typography>
            <div v-tooltip.left="candidate.lockedForMerge ? candidate.lockedReason : ''" class="inline-flex">
              <Button
                label="Combine this Contact"
                size="small"
                outlined
                :disabled="candidate.lockedForMerge || props.isLoading"
                @click="emit('select', candidate.id)"
              />
            </div>
          </Stack>
        </ContactCard>
      </Stack>
    </div>
    <Stack :gap="2">
      <Typography variant="section-heading">
        Search for a different contact:
      </Typography>
      <ContactAutoComplete
        v-model="searchedContact"
        placeholder="Search contacts by name"
        :disabled="props.isLoading"
      />
    </Stack>
  </Stack>
</template>
